import React from 'react';
import { fetchMessages } from '../../../api/agentChat/threads/client';
import { fetchEvents } from '../../../api/events/client';
import { type DisplayMessage, reconcileProposals } from '../chatHelpers';

interface Result {
  displayMessages: DisplayMessage[];
  setDisplayMessages: React.Dispatch<React.SetStateAction<DisplayMessage[]>>;
  displayMessagesRef: React.MutableRefObject<DisplayMessage[]>;
  loading: boolean;
}

// Owns the chat-history state machine: message list state, mirrored ref for
// closures that need the latest value without re-running, and the initial
// history fetch (with proposal reconciliation when any approved proposal is in
// the loaded set).
//
// The StrictMode dev double-fire of this effect is suppressed by a
// per-(dossierId,threadId) ref. EscChat remounts on workspace switch via
// `key={threadId}`, so this ref starts fresh for legitimate switches.
export function useChatMessages(
  dossierId: string,
  threadId: string,
  handleThreadGone: (err: unknown) => boolean
): Result {
  const [displayMessages, setDisplayMessages] = React.useState<
    DisplayMessage[]
  >([]);
  const [loading, setLoading] = React.useState(true);

  const displayMessagesRef = React.useRef<DisplayMessage[]>([]);
  React.useEffect(() => {
    displayMessagesRef.current = displayMessages;
  }, [displayMessages]);

  const messagesFetchedForRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!dossierId) {
      return;
    }
    const key = `${dossierId}:${threadId}`;
    if (messagesFetchedForRef.current === key) {
      return;
    }
    messagesFetchedForRef.current = key;
    setLoading(true);
    (async () => {
      try {
        const res = await fetchMessages(dossierId, threadId);
        const loaded: DisplayMessage[] = res.messages.map((m) => ({
          message_id: m.message_id,
          role: m.role,
          text: m.text,
          created_at: m.created_at,
          proposals: m.proposals,
          artifact_id: m.artifact_id,
          artifact_name: m.artifact_name,
        }));
        setDisplayMessages(loaded);

        const hasApproved = loaded.some((m) =>
          m.proposals?.some((p) => p.status === 'approved')
        );
        if (!hasApproved) {
          return;
        }
        const events = await fetchEvents(dossierId);
        setDisplayMessages((prev) => reconcileProposals(prev, events));
      } catch (err) {
        if (!handleThreadGone(err)) {
          // Non-thread-gone failure (network, 5xx, etc.): clear the
          // dedupe ref so the next mount for this (dossierId, threadId)
          // can retry instead of staying empty forever.
          messagesFetchedForRef.current = null;
        }
      } finally {
        setLoading(false);
      }
    })();
    // handleThreadGone identity changes on every render via useCallback's
    // dep on `onThreadGone`; we only want re-fetch on (dossierId, threadId).
    // eslint-disable-next-line react-hooks2/exhaustive-deps
  }, [dossierId, threadId]);

  return { displayMessages, setDisplayMessages, displayMessagesRef, loading };
}
